/**
 * Service to handle fetching and managing term child terms
 */
import { termsStore } from '../stores/termsStore.js';
import { backendService } from './backendService.js';

export class TermChildService {
    constructor(termsStore) {
        this.termsStore = termsStore;
        this.cache = new Map(); // Local cache to avoid redundant API calls
    }
    
    /**
     * Fetch child terms for a term from the backend
     * @param {string} term - The term to fetch child terms for
     * @returns {Promise<string[]>} - A promise resolving to an array of child terms
     */
    async getChildTerms(term) {
        // Check if child terms are already in the cache
        if (this.cache.has(term)) {
            console.log(`[TermChildService] Using cached child terms for "${term}"`);
            return this.cache.get(term);
        }
        
        const startTime = Date.now();
        
        try {
            console.log(`[TermChildService] Starting fetch for child terms of "${term}"`);
            
            // Set loading state for this term
            this.termsStore.setFetchingChildTerms(term, true);
            this.termsStore.setOperationLoading(`child-terms-${term}`, true, {
                type: 'child-terms',
                term: term
            });
            
            // Always use the backend API regardless of environment
            console.log('[TermChildService] Calling backend API for child terms');
            const response = await backendService.fetchTermChildTerms(term);
            const childTerms = response.child_terms || [];
            
            const elapsed = Date.now() - startTime;
            console.log(`[TermChildService] Received ${childTerms.length} child terms for "${term}" in ${elapsed}ms:`, childTerms);
            
            // Update cache
            this.cache.set(term, childTerms);
            
            // Update store
            this.termsStore.setChildTerms(term, childTerms);
            
            return childTerms;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`[TermChildService] Error fetching child terms for "${term}" after ${elapsed}ms:`, error);
            
            // Return empty array if API fails
            console.log('[TermChildService] API failed, returning empty array');
            return [];
        } finally {
            // Always clear loading state
            this.termsStore.setFetchingChildTerms(term, false);
            this.termsStore.setOperationLoading(`child-terms-${term}`, false);
            
            const totalElapsed = Date.now() - startTime;
            console.log(`[TermChildService] Completed child terms operation for "${term}" in ${totalElapsed}ms`);
        }
    }
    
    /**
     * Clear cached child terms for a term
     * @param {string} term - Term to clear child terms for
     */
    clearCachedChildTerms(term) {
        this.cache.delete(term);
        this.termsStore.clearChildTerms(term);
    }
    
    /**
     * Clear all cached child terms
     */
    clearAllCachedChildTerms() {
        this.cache.clear();
        
        // We need to clear child terms from the store as well
        // This requires reading all terms and clearing each individually
        this.termsStore.update(store => {
            const newChildTermsMap = new Map();
            return {
                ...store,
                childTermsMap: newChildTermsMap
            };
        });
    }
}

// Create singleton instance
export const termChildService = new TermChildService(termsStore);